const DATE_ONLY_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export function parseDateOnly(value) {
	const match = DATE_ONLY_PATTERN.exec(value || '');

	if (!match) {
		return null;
	}

	const year = Number(match[1]);
	const month = Number(match[2]);
	const day = Number(match[3]);
	const date = new Date(Date.UTC(year, month - 1, day));

	if (
		date.getUTCFullYear() !== year ||
		date.getUTCMonth() !== month - 1 ||
		date.getUTCDate() !== day
	) {
		return null;
	}

	return {day, month, year};
}

export function isValidDateOnly(value) {
	return parseDateOnly(value) !== null;
}

export function toDateOnlyString(dateOnly) {
	const year = String(dateOnly.year).padStart(4, '0');
	const month = String(dateOnly.month).padStart(2, '0');
	const day = String(dateOnly.day).padStart(2, '0');

	return `${year}-${month}-${day}`;
}

export function toUTCDate(dateOnly) {
	return new Date(Date.UTC(dateOnly.year, dateOnly.month - 1, dateOnly.day));
}

export function addDays(dateOnly, days) {
	const date = toUTCDate(dateOnly);
	date.setUTCDate(date.getUTCDate() + days);

	return {
		day: date.getUTCDate(),
		month: date.getUTCMonth() + 1,
		year: date.getUTCFullYear(),
	};
}

export function diffInCalendarDays(laterDateOnly, earlierDateOnly) {
	return Math.floor(
		(toUTCDate(laterDateOnly).getTime() - toUTCDate(earlierDateOnly).getTime()) /
			MS_PER_DAY
	);
}

export function getTodayDateOnly() {
	const now = new Date();

	return {
		day: now.getDate(),
		month: now.getMonth() + 1,
		year: now.getFullYear(),
	};
}

export function formatDate(dateOnly, locale = 'en-US', format = 'long') {
	const date = toUTCDate(dateOnly);

	const optionsByFormat = {
		day: {day: 'numeric'},
		long: {day: 'numeric', month: 'long', year: 'numeric'},
		month: {month: 'long'},
		shortMonth: {month: 'short'},
		shortMonthDay: {day: 'numeric', month: 'short'},
		year: {year: 'numeric'},
	};

	return new Intl.DateTimeFormat(locale, {
		timeZone: 'UTC',
		...optionsByFormat[format],
	}).format(date);
}

export const TRANSFER_TYPES = [
	{
		labelKey: 'due-date-calculator.transfer-type.lmp-natural-iui',
		offset: 282,
		value: 'lmp-natural-iui',
	},
	{
		labelKey: 'due-date-calculator.transfer-type.fresh-lh-surge',
		offset: 268,
		value: 'fresh-lh-surge',
	},
	{
		labelKey: 'due-date-calculator.transfer-type.frozen-day-5-6-7',
		offset: 263,
		value: 'frozen-day-5-6-7',
	},
];

export function calculateDueDate(referenceDateOnly, transferTypeValue, todayDateOnly) {
	const transferType = TRANSFER_TYPES.find(
		(option) => option.value === transferTypeValue
	);

	if (!referenceDateOnly || !transferType) {
		return null;
	}

	const estimatedDueDate = addDays(referenceDateOnly, transferType.offset);
	const pregnancyStartDate = addDays(estimatedDueDate, -281);
	const gestationalAgeDays = diffInCalendarDays(
		todayDateOnly || getTodayDateOnly(),
		pregnancyStartDate
	);
	const gestationalWeeks = Math.floor(gestationalAgeDays / 7);
	const gestationalDays = gestationalAgeDays % 7;
	const firstTrimesterEnd = addDays(pregnancyStartDate, 93);
	const secondTrimesterStart = addDays(firstTrimesterEnd, 1);
	const secondTrimesterEnd = addDays(secondTrimesterStart, 93);
	const thirdTrimesterStart = addDays(secondTrimesterEnd, 1);

	return {
		estimatedDueDate,
		gestationalAgeDays,
		gestationalDays,
		gestationalWeeks,
		pregnancyStartDate,
		trimesterRanges: [
			{
				from: pregnancyStartDate,
				labelKey: 'due-date-calculator.trimester.first',
				to: firstTrimesterEnd,
			},
			{
				from: secondTrimesterStart,
				labelKey: 'due-date-calculator.trimester.second',
				to: secondTrimesterEnd,
			},
			{
				from: thirdTrimesterStart,
				labelKey: 'due-date-calculator.trimester.third',
				to: estimatedDueDate,
			},
		],
	};
}
